// ============================================================================
//  InvoicesPages.tsx — Module Facturation NexusCorp ERP
//  Pages : Dashboard, Liste, Détail, Nouvelle facture, Avoir
//  Conformité CGI SN — compliance-senegal.md §3
// ============================================================================

import { useState, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { toNum } from '@/lib/decimal'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, Routes, Route } from 'react-router-dom'
import {
  FileText, Plus, Search, Filter, Send, CheckCircle, XCircle,
  AlertCircle, Clock, DollarSign, TrendingUp, Download,
  ChevronRight, Eye, MoreVertical, RefreshCw, CreditCard,
  Building2, Mail, Hash, Calendar, Lock, Unlock, ArrowLeft,
  Receipt, AlertTriangle, Trash2, Check, X, ChevronDown,
  Pencil, Printer,
} from 'lucide-react'
import {
  invoicesApi,
  type Invoice, type InvoiceStatus, type InvoiceType,
  type CreateInvoiceInput, type CreateCreditNoteInput,
  type RecordPaymentInput, type ListInvoicesParams, type SalesTaxRule,
} from '@/modules/invoices/api/invoices.api'
import { useNotificationStore } from '@/stores/notification.store'
import { usePermission }        from '@/modules/auth/hooks/usePermission'
import api                      from '@/lib/axios'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtMoney = (n?: any) =>
  n !== undefined && n !== null
    ? new Intl.NumberFormat('fr-SN', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }).format(toNum(n))
    : '—'

const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('fr-FR') : '—'

const STATUS_CONFIG: Record<InvoiceStatus, { label: string; color: string; icon: typeof Clock }> = {
  DRAFT:        { label: 'Brouillon',     color: 'bg-[#D6D2EC]/20 text-[var(--tx2)] border-[var(--tx3)]/30',     icon: Clock },
  ISSUED:       { label: 'Émise',         color: 'bg-[#BAE6FD] text-blue-300 border-blue-500/30',         icon: Send },
  SENT:         { label: 'Envoyée',       color: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',   icon: Mail },
  PARTIAL_PAID: { label: 'Paiement part.', color: 'bg-[#FFF7ED] text-[#EA580C] border-[rgba(249,115,22,0.3)]', icon: CreditCard },
  PAID:         { label: 'Soldée',        color: 'bg-[#10B981]/20 text-emerald-300 border-emerald-500/30', icon: CheckCircle },
  CANCELLED:    { label: 'Annulée',       color: 'bg-red-500/20 text-red-300 border-[rgba(239,68,68,0.35)]',             icon: XCircle },
  DISPUTED:     { label: 'Contestée',     color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',   icon: AlertCircle },
}

const TYPE_LABELS: Record<InvoiceType, string> = {
  INVOICE:    'Facture',
  CREDIT_NOTE: 'Avoir',
  DEBIT_NOTE:  'Note de débit',
  PROFORMA:   'Proforma',
  ADVANCE:    'Acompte',
}

const Spinner = () => <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />

function StatusBadge({ status }: { status: InvoiceStatus }) {
  const c = STATUS_CONFIG[status]
  if (!c) return null
  const Icon = c.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${c.color}`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  )
}

// ============================================================================
//  DASHBOARD FACTURATION
// ============================================================================

function InvoiceDashboard() {
  const navigate = useNavigate()
  const { can }  = usePermission()

  const { data: stats, isLoading } = useQuery({
    queryKey: ['invoice-stats'],
    queryFn:  () => invoicesApi.getStats(),
  })

  const { data: recent } = useQuery({
    queryKey: ['invoices-recent'],
    queryFn:  () => invoicesApi.list({ limit: 5, page: 1 }),
  })

  const STAT_CARDS = [
    { label: 'Brouillons',        value: stats?.draft,        color: 'text-[var(--tx2)]', bg: 'bg-[#D6D2EC]/10', icon: FileText },
    { label: 'En attente',        value: stats?.issued,       color: 'text-[var(--blue-d)]',  bg: 'bg-[#E0F2FE]',  icon: Send },
    { label: 'Soldées',           value: stats?.paid,         color: 'text-[#059669]', bg: 'bg-[#10B981]/10', icon: CheckCircle },
    { label: 'En retard',         value: stats?.overdue,      color: 'text-[#DC2626]',   bg: 'bg-[#FEF2F2]',   icon: AlertTriangle },
  ]

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[var(--brand-dark)]">Facturation</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Conforme CGI Sénégal — numérotation séquentielle inaltérable</p>
        </div>
        {can('invoices:create') && (
          <button
            onClick={() => navigate('/invoices/new')}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvelle facture
          </button>
        )}
      </div>

      {/* Revenue banner */}
      <div className="bg-gradient-to-r from-blue-600/20 to-indigo-600/20 border border-[rgba(245,124,0,0.25)] rounded-xl p-6">
        <p className="text-sm text-blue-300 font-medium mb-1">Chiffre d'affaires encaissé</p>
        {isLoading ? (
          <div className="h-9 w-48 bg-[var(--surface-2)] rounded animate-pulse" />
        ) : (
          <p className="text-3xl font-black text-[var(--brand-dark)]">{fmtMoney(stats?.totalRevenue)}</p>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {STAT_CARDS.map(s => (
          <div key={s.label} className={`${s.bg} border border-[var(--bd)] rounded-xl p-4`}>
            <div className="flex items-center gap-3">
              <s.icon className={`w-5 h-5 ${s.color}`} />
              <div>
                <p className="text-2xl font-bold text-[var(--brand-dark)]">{isLoading ? '…' : (s.value ?? 0)}</p>
                <p className="text-xs text-[var(--tx3)]">{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Factures récentes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-[var(--tx2)]">Dernières factures</h2>
          <button onClick={() => navigate('/invoices/list')} className="text-xs text-[var(--blue-d)] hover:text-blue-300 flex items-center gap-1">
            Voir tout <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
          {!recent?.data.length ? (
            <div className="p-8 text-center text-[var(--tx3)] text-sm">Aucune facture</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--bd)]">
                  {['Numéro', 'Client', 'Type', 'Montant TTC', 'Date', 'Statut'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs text-[var(--tx3)] font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recent.data.map(inv => (
                  <tr
                    key={inv.id}
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                    className="border-b border-[var(--bd)] hover:bg-[var(--surface-2)] cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-blue-300 text-xs">{inv.number}</td>
                    <td className="px-4 py-3 text-[var(--brand-dark)]">{inv.clientName}</td>
                    <td className="px-4 py-3 text-[var(--tx3)]">{TYPE_LABELS[inv.type]}</td>
                    <td className="px-4 py-3 font-semibold text-[var(--brand-dark)]">{fmtMoney(inv.totalTtc)}</td>
                    <td className="px-4 py-3 text-[var(--tx3)]">{fmtDate(inv.issueDate)}</td>
                    <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
//  LISTE FACTURES
// ============================================================================

function InvoiceListPage() {
  const navigate = useNavigate()
  const { can }  = usePermission()
  const [params, setParams] = useState<ListInvoicesParams>({ page: 1, limit: 20 })
  const [search, setSearch] = useState('')

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['invoices', params, search],
    queryFn:  () => invoicesApi.list({ ...params, search: search || undefined }),
  })

  const STATUS_FILTERS: Array<{ value?: InvoiceStatus; label: string }> = [
    { label: 'Toutes' },
    { value: 'DRAFT',        label: 'Brouillons' },
    { value: 'ISSUED',       label: 'Émises' },
    { value: 'SENT',         label: 'Envoyées' },
    { value: 'PARTIAL_PAID', label: 'Part. payées' },
    { value: 'PAID',         label: 'Soldées' },
    { value: 'OVERDUE' as any, label: 'En retard' },
    { value: 'CANCELLED',    label: 'Annulées' },
  ]

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/invoices')} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-xl font-bold text-[var(--brand-dark)]">Factures</h1>
          {data && <span className="text-xs text-[var(--tx3)] bg-white px-2 py-0.5 rounded-full">{data.meta.total}</span>}
        </div>
        <div className="flex items-center gap-2">
          {can('invoices:export') && (
            <button
              onClick={() => navigate('/invoices/export-fec')}
              className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-[var(--surface-2)] text-[var(--tx2)] rounded-lg text-sm transition-colors"
            >
              <Download className="w-4 h-4" />
              Export FEC
            </button>
          )}
          {can('invoices:create') && (
            <button
              onClick={() => navigate('/invoices/new')}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nouvelle
            </button>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-[var(--tx3)] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par numéro, client, NINEA…"
            className="w-full pl-9 pr-4 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] placeholder-[var(--tx3)] focus:outline-none focus:border-blue-500"
          />
        </div>
        <div className="flex items-center gap-1">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.label}
              onClick={() => setParams(p => ({ ...p, status: f.value, page: 1 }))}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                params.status === f.value
                  ? 'bg-[var(--blue)] text-white'
                  : 'bg-white text-[var(--tx3)] hover:text-[var(--brand-dark)] hover:bg-[var(--surface-2)]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center p-12"><Spinner /></div>
        ) : !data?.data.length ? (
          <div className="p-12 text-center">
            <FileText className="w-8 h-8 text-[var(--tx3)] mx-auto mb-3" />
            <p className="text-[var(--tx3)]">Aucune facture trouvée</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Numéro', 'Client', 'NINEA', 'Type', 'Montant TTC', 'Échéance', 'Statut', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs text-[var(--tx3)] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map(inv => {
                const isOverdue = inv.dueDate && ['ISSUED', 'SENT', 'PARTIAL_PAID'].includes(inv.status)
                  && new Date(inv.dueDate) < new Date()
                return (
                  <tr
                    key={inv.id}
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                    className={`border-b border-[var(--bd)] cursor-pointer transition-colors ${
                      isOverdue ? 'hover:bg-red-500/5' : 'hover:bg-[var(--surface-2)]'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-blue-300 text-xs">{inv.number}</span>
                        {inv.isNumberLocked && <Lock className="w-3 h-3 text-[var(--tx3)]" title="Numéro verrouillé (CGI SN)" />}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[var(--brand-dark)] font-medium">{inv.clientName}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[var(--tx3)]">{inv.clientNinea || '—'}</td>
                    <td className="px-4 py-3 text-[var(--tx3)]">{TYPE_LABELS[inv.type]}</td>
                    <td className="px-4 py-3 font-bold text-[var(--brand-dark)]">{fmtMoney(inv.totalTtc)}</td>
                    <td className={`px-4 py-3 text-xs ${isOverdue ? 'text-[#DC2626] font-medium' : 'text-[var(--tx3)]'}`}>
                      {fmtDate(inv.dueDate)}
                      {isOverdue && <span className="ml-1">(retard)</span>}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                    <td className="px-4 py-3">
                      <ChevronRight className="w-4 h-4 text-[var(--tx3)]" />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {data && data.meta.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--bd)]">
            <p className="text-xs text-[var(--tx3)]">
              {((data.meta.page - 1) * data.meta.limit) + 1}–{Math.min(data.meta.page * data.meta.limit, data.meta.total)} sur {data.meta.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                disabled={data.meta.page <= 1}
                onClick={() => setParams(p => ({ ...p, page: (p.page ?? 1) - 1 }))}
                className="px-3 py-1.5 bg-white text-[var(--tx3)] rounded text-xs disabled:opacity-40 hover:bg-[var(--surface-2)]"
              >Préc.</button>
              <span className="text-xs text-[var(--tx3)]">{data.meta.page}/{data.meta.totalPages}</span>
              <button
                disabled={data.meta.page >= data.meta.totalPages}
                onClick={() => setParams(p => ({ ...p, page: (p.page ?? 1) + 1 }))}
                className="px-3 py-1.5 bg-white text-[var(--tx3)] rounded text-xs disabled:opacity-40 hover:bg-[var(--surface-2)]"
              >Suiv.</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
//  DÉTAIL FACTURE
// ============================================================================


// ─── Ouvrir PDF (nouvel onglet) ────────────────────────────────────────────
async function openInvoicePdf(invoiceId: string) {
  // Onglet ouvert de façon synchrone dans le clic (sinon bloqué comme pop-up),
  // puis redirigé une fois le jeton d'impression obtenu.
  const win = window.open('about:blank', '_blank')
  if (!win) {
    // Pop-up bloquée : ne pas naviguer dans l'onglet de l'application (« Fermer »
    // y serait inopérant) ; aucun jeton n'est émis.
    useNotificationStore.getState().addNotification({
      title:   'Fenêtre bloquée',
      message: 'Autorisez les fenêtres pop-up pour imprimer la facture.',
      type:    'warning',
    })
    return
  }
  win.opener = null
  try {
    // [M1] Jeton d'impression court et à usage unique — jamais l'access token dans l'URL.
    const { token } = await invoicesApi.getPrintToken(invoiceId)
    // Même base que les appels API (VITE_API_URL, ou chemin relatif derrière Nginx / proxy Vite).
    // Jamais de localhost en dur : en production VITE_API_URL est vide et l'origine courante fait foi.
    const base = api.defaults.baseURL ?? '/api/v1'
    const url  = new URL(
      `${base}/invoices/${invoiceId}/html?pt=${encodeURIComponent(token)}`,
      window.location.origin,
    ).href
    win.location.href = url
  } catch (e: any) {
    win.close()
    useNotificationStore.getState().addNotification({
      title:   'Erreur',
      message: e?.response?.data?.message ?? "Impossible d'ouvrir le PDF de la facture.",
      type:    'error',
    })
  }
}

// ─── API delete draft ──────────────────────────────────────────────────────
const deleteDraftApi = (id: string) =>
  api.delete(`/invoices/${id}`).then(r => r.data.data ?? r.data)

// ─── API send email ────────────────────────────────────────────────────────
const sendInvoiceEmailApi = (id: string) =>
  api.post(`/invoices/${id}/send`).then(r => r.data.data ?? r.data)

function InvoiceDetailPage() {
  const { id }   = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const { can }  = usePermission()
  const { addNotification } = useNotificationStore()
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false)
  const [paymentData, setPaymentData] = useState<RecordPaymentInput>({
    amount: 0, method: 'BANK_TRANSFER', paidAt: new Date().toISOString().split('T')[0],
  })

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn:  () => invoicesApi.getOne(id!),
    enabled:  !!id,
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteDraftApi(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice-stats'] })
      addNotification({ id: Date.now().toString(), title: 'Brouillon supprimé', message: 'La facture a été supprimée.', type: 'info' })
      navigate('/invoices')
    },
    onError: (e: any) => addNotification({ id: Date.now().toString(), title: 'Erreur', message: e.response?.data?.message ?? 'Erreur', type: 'error' }),
  })

  const emailMutation = useMutation({
    mutationFn: () => sendInvoiceEmailApi(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] })
      addNotification({ id: Date.now().toString(), title: 'Email envoyé', message: `Facture envoyée à ${invoice?.clientEmail}`, type: 'success' })
    },
    onError: (e: any) => addNotification({ id: Date.now().toString(), title: 'Erreur', message: e.response?.data?.message ?? 'Erreur envoi email', type: 'error' }),
  })

  const issueMutation = useMutation({
    mutationFn: () => invoicesApi.issue(id!),
    onSuccess: (issued) => {
      qc.invalidateQueries({ queryKey: ['invoice', id] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice-stats'] })
      addNotification({ id: Date.now().toString(), title: 'Facture émise', message: `Numéro ${invoice?.number} verrouillé définitivement (CGI SN)`, type: 'success' })
      // Émission non bloquante sans NINEA : on prévient l'utilisateur (CGI SN art. 356)
      const emitted = issued ?? invoice
      if (emitted && toNum(emitted.totalTax) > 0 && !emitted.clientNinea) {
        addNotification({
          title:   'Facture émise sans NINEA client',
          message: 'La TVA est facturée, mais le client ne pourra pas la déduire (CGI SN art. 356).',
          type:    'warning',
        })
      }
    },
    onError: (e: any) => addNotification({ id: Date.now().toString(), title: 'Erreur', message: e.response?.data?.message ?? 'Erreur lors de l\'émission', type: 'error' }),
  })

  const sentMutation = useMutation({
    mutationFn: () => invoicesApi.markAsSent(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] })
      addNotification({ id: Date.now().toString(), title: 'Facture envoyée', message: 'Statut mis à jour', type: 'success' })
    },
  })

  const paymentMutation = useMutation({
    mutationFn: () => invoicesApi.recordPayment(id!, paymentData),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoice', id] })
      qc.invalidateQueries({ queryKey: ['invoice-stats'] })
      setShowPaymentForm(false)
      addNotification({ id: Date.now().toString(), title: 'Paiement enregistré', message: fmtMoney(paymentData.amount), type: 'success' })
    },
    onError: (e: any) => addNotification({ id: Date.now().toString(), title: 'Erreur', message: e.response?.data?.message ?? 'Erreur', type: 'error' }),
  })

  if (isLoading) return <div className="flex items-center justify-center p-12"><Spinner /></div>
  if (!invoice)  return <div className="p-6 text-[#DC2626]">Facture introuvable</div>

  const totalPaid   = invoice.payments.reduce((s, p) => s + toNum(p.amount), 0)
  const remaining   = toNum(invoice.totalTtc) - totalPaid
  const payProgress = toNum(invoice.totalTtc) > 0 ? (totalPaid / toNum(invoice.totalTtc)) * 100 : 0

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-[var(--brand-dark)] font-mono">{invoice.number}</h1>
              {invoice.isNumberLocked && (
                <span className="flex items-center gap-1 text-xs text-[var(--tx3)] bg-white px-2 py-0.5 rounded-full">
                  <Lock className="w-3 h-3" /> Verrouillée (CGI SN)
                </span>
              )}
            </div>
            <p className="text-sm text-[var(--tx3)]">{TYPE_LABELS[invoice.type]} · {invoice.clientName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={invoice.status} />
          {/* Actions */}
          {/* PDF — toutes factures non-draft */}
          {invoice.status !== 'DRAFT' && (
            <button
              onClick={() => openInvoicePdf(invoice.id)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm font-medium text-[var(--tx2)] hover:bg-[var(--surface-2)] transition-colors"
            >
              <Printer className="w-4 h-4" />
              PDF
            </button>
          )}



          {/* Supprimer brouillon */}
          {invoice.status === 'DRAFT' && can('invoices:create') && (
            <button
              onClick={() => { if (window.confirm('Supprimer ce brouillon ?')) deleteMutation.mutate() }}
              disabled={deleteMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm font-medium text-[var(--red-text)] hover:bg-[var(--red-bg)] transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Supprimer
            </button>
          )}

          {invoice.status === 'DRAFT' && can('invoices:issue') && (
            <button
              onClick={() => issueMutation.mutate()}
              disabled={issueMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {issueMutation.isPending ? <Spinner /> : <Send className="w-4 h-4" />}
              Émettre
            </button>
          )}
          {/* Envoi — un seul bouton intelligent selon présence email client */}
          {invoice.status === 'ISSUED' && can('invoices:issue') && (
            <button
              onClick={() => invoice.clientEmail ? emailMutation.mutate() : sentMutation.mutate()}
              disabled={emailMutation.isPending || sentMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 bg-[#6366F1] hover:bg-indigo-500 text-[var(--brand-dark)] rounded-lg text-sm font-medium transition-colors"
            >
              {invoice.clientEmail ? <Mail className="w-4 h-4" /> : <Send className="w-4 h-4" />}
              {emailMutation.isPending || sentMutation.isPending
                ? 'Envoi…'
                : invoice.clientEmail ? 'Envoyer par email' : 'Marquer envoyée'
              }
            </button>
          )}
          {['ISSUED', 'SENT', 'PARTIAL_PAID'].includes(invoice.status) && can('invoices:update') && (
            <button
              onClick={() => setShowPaymentForm(true)}
              className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-[#10B981] text-[var(--brand-dark)] rounded-lg text-sm font-medium transition-colors"
            >
              <CreditCard className="w-4 h-4" /> Paiement
            </button>
          )}
          {['ISSUED', 'SENT', 'PARTIAL_PAID', 'PAID'].includes(invoice.status) && can('invoices:cancel') && (
            <button
              onClick={() => setShowCreditNoteModal(true)}
              className="flex items-center gap-2 px-3 py-2 bg-[var(--surface-2)] hover:bg-[var(--bd)] text-[var(--tx2)] rounded-lg text-sm transition-colors"
            >
              <Receipt className="w-4 h-4" /> Avoir
            </button>
          )}
        </div>
      </div>

      {/* Avertissement avoir */}
      {invoice.type === 'CREDIT_NOTE' && invoice.originalInvoice && (
        <div className="flex items-center gap-3 bg-[#FEF3C7] border border-[rgba(245,158,11,0.25)] rounded-xl p-4">
          <AlertTriangle className="w-5 h-5 text-[#D97706] shrink-0" />
          <p className="text-sm text-amber-300">
            Avoir sur la facture <span className="font-mono font-bold">{invoice.originalInvoice.number}</span>
          </p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Infos client + montants */}
        <div className="col-span-2 space-y-4">

          {/* Client */}
          <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-[var(--tx3)] mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Informations client
            </h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-[var(--tx3)] text-xs">Raison sociale</p><p className="text-[var(--brand-dark)] font-medium">{invoice.clientName}</p></div>
              <div>
                <p className="text-[var(--tx3)] text-xs flex items-center gap-1">
                  NINEA {!invoice.clientNinea && toNum(invoice.totalTax) > 0 && <AlertTriangle className="w-3 h-3 text-[#D97706]" aria-hidden="true" />}
                </p>
                <p className={`font-mono ${invoice.clientNinea ? 'text-[var(--brand-dark)]' : 'text-[var(--tx3)]'}`}>
                  {invoice.clientNinea || 'Non renseigné'}
                  {!invoice.clientNinea && toNum(invoice.totalTax) > 0 && <span className="text-[#D97706] text-xs ml-1">(NINEA manquant)</span>}
                </p>
              </div>
              {invoice.clientRccm && <div><p className="text-[var(--tx3)] text-xs">RCCM</p><p className="font-mono text-[var(--tx2)]">{invoice.clientRccm}</p></div>}
              {invoice.clientEmail && <div><p className="text-[var(--tx3)] text-xs">Email</p><p className="text-[var(--tx2)]">{invoice.clientEmail}</p></div>}
              {invoice.clientAddress && <div className="col-span-2"><p className="text-[var(--tx3)] text-xs">Adresse</p><p className="text-[var(--tx2)]">{invoice.clientAddress}</p></div>}
            </div>
          </div>

          {/* Lignes */}
          <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-[var(--bd)]">
              <h2 className="text-sm font-semibold text-[var(--tx3)]">Lignes de facturation</h2>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--bd)]">
                  {['Description', 'Qté', 'P.U. HT', 'Remise', 'TVA', 'Montant TTC'].map(h => (
                    <th key={h} className="px-4 py-2 text-left text-xs text-[var(--tx3)] font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoice.lines.map(line => (
                  <tr key={line.id} className="border-b border-[var(--bd)] last:border-0">
                    <td className="px-4 py-3 text-[var(--brand-dark)]">{line.description}</td>
                    <td className="px-4 py-3 text-[var(--tx3)]">{toNum(line.quantity)}</td>
                    <td className="px-4 py-3 text-[var(--tx2)]">{fmtMoney(line.unitPrice)}</td>
                    <td className="px-4 py-3 text-[var(--tx3)]">{toNum(line.discount) > 0 ? `${toNum(line.discount)}%` : '—'}</td>
                    <td className="px-4 py-3 text-[var(--tx3)]">{toNum(line.taxRate) > 0 ? `${toNum(line.taxRate)}%` : '—'}</td>
                    <td className="px-4 py-3 font-semibold text-[var(--brand-dark)]">{fmtMoney(line.amountTtc)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-[var(--bd)] bg-[var(--bg)]">
                  <td colSpan={5} className="px-4 py-2 text-right text-xs text-[var(--tx3)]">Total HT</td>
                  <td className="px-4 py-2 font-semibold text-[var(--brand-dark)]">{fmtMoney(invoice.subtotalHt)}</td>
                </tr>
                <tr className="bg-[var(--bg)]">
                  <td colSpan={5} className="px-4 py-2 text-right text-xs text-[var(--tx3)]">TVA</td>
                  <td className="px-4 py-2 font-semibold text-blue-300">{fmtMoney(invoice.totalTax)}</td>
                </tr>
                <tr className="bg-[var(--surface-2)]">
                  <td colSpan={5} className="px-4 py-2 text-right text-sm font-bold text-[var(--tx2)]">Total TTC</td>
                  <td className="px-4 py-2 text-lg font-black text-[var(--brand-dark)]">{fmtMoney(invoice.totalTtc)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Sidebar droite */}
        <div className="space-y-4">
          {/* Dates */}
          <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-4 space-y-3">
            <h2 className="text-sm font-semibold text-[var(--tx3)]">Dates</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[var(--tx3)]">Émission</span><span className="text-[var(--brand-dark)]">{fmtDate(invoice.issuedAt || invoice.issueDate)}</span></div>
              <div className="flex justify-between"><span className="text-[var(--tx3)]">Échéance</span>
                <span className={invoice.dueDate && new Date(invoice.dueDate) < new Date() && !['PAID', 'CANCELLED'].includes(invoice.status) ? 'text-[#DC2626] font-medium' : 'text-[var(--brand-dark)]'}>
                  {fmtDate(invoice.dueDate)}
                </span>
              </div>
              <div className="flex justify-between"><span className="text-[var(--tx3)]">Créée le</span><span className="text-[var(--tx2)]">{fmtDate(invoice.createdAt)}</span></div>
            </div>
          </div>

          {/* Paiements */}
          <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-4 space-y-3">
            <h2 className="text-sm font-semibold text-[var(--tx3)]">Règlements</h2>
            {/* Barre progression */}
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-[var(--tx3)]">Payé</span>
                <span className="text-[var(--brand-dark)] font-medium">{Math.round(payProgress)}%</span>
              </div>
              <div className="h-2 bg-[var(--surface-2)] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#10B981] rounded-full transition-all"
                  style={{ width: `${Math.min(payProgress, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-[#059669]">{fmtMoney(totalPaid)}</span>
                <span className="text-[var(--tx3)]">Restant : {fmtMoney(Math.max(remaining, 0))}</span>
              </div>
            </div>
            {invoice.payments.length > 0 && (
              <div className="space-y-2 mt-2">
                {invoice.payments.map(p => (
                  <div key={p.id} className="flex items-center justify-between text-xs">
                    <div>
                      <span className="text-[var(--brand-dark)] font-medium">{fmtMoney(toNum(p.amount))}</span>
                      <span className="text-[var(--tx3)] ml-2">{p.method.replace('_', ' ')}</span>
                    </div>
                    <span className="text-[var(--tx3)]">{fmtDate(p.paidAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal paiement */}
      {showPaymentForm && (
        <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
          <div className="bg-white border border-[var(--bd)] rounded-2xl p-6 w-full max-w-2xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-[var(--brand-dark)]">Enregistrer un paiement</h3>
              <button onClick={() => setShowPaymentForm(false)} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[var(--tx3)] block mb-1">Montant (XOF)</label>
                <input
                  type="number"
                  value={paymentData.amount}
                  onChange={e => setPaymentData(p => ({ ...p, amount: Number(e.target.value) }))}
                  className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] text-sm focus:outline-none focus:border-blue-500"
                  max={remaining}
                />
              </div>
              <div>
                <label className="text-xs text-[var(--tx3)] block mb-1">Moyen de paiement</label>
                <select
                  value={paymentData.method}
                  onChange={e => setPaymentData(p => ({ ...p, method: e.target.value as any }))}
                  className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="BANK_TRANSFER">Virement bancaire</option>
                  <option value="CHECK">Chèque</option>
                  <option value="CASH">Espèces</option>
                  <option value="MOBILE_MONEY">Mobile Money (Wave/Orange)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[var(--tx3)] block mb-1">Date de paiement</label>
                <input
                  type="date"
                  value={paymentData.paidAt}
                  onChange={e => setPaymentData(p => ({ ...p, paidAt: e.target.value }))}
                  className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-xs text-[var(--tx3)] block mb-1">Référence (optionnel)</label>
                <input
                  type="text"
                  value={paymentData.reference || ''}
                  onChange={e => setPaymentData(p => ({ ...p, reference: e.target.value }))}
                  placeholder="Réf. virement, numéro chèque…"
                  className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowPaymentForm(false)} className="flex-1 py-2 bg-white hover:bg-[var(--surface-2)] text-[var(--tx2)] rounded-lg text-sm transition-colors">
                Annuler
              </button>
              <button
                onClick={() => paymentMutation.mutate()}
                disabled={paymentMutation.isPending || !paymentData.amount}
                className="flex-1 py-2 bg-emerald-600 hover:bg-[#10B981] text-[var(--brand-dark)] rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {paymentMutation.isPending ? <Spinner /> : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal Avoir ──────────────────────────────────────────────────── */}
      {showCreditNoteModal && (
        <CreditNoteModal
          invoice={invoice}
          onClose={() => setShowCreditNoteModal(false)}
          onCreated={(creditNote) => {
            qc.invalidateQueries({ queryKey: ['invoices'] })
            qc.invalidateQueries({ queryKey: ['invoice-stats'] })
            setShowCreditNoteModal(false)
            navigate(`/invoices/${creditNote.id}`)
          }}
        />
      )}
    </div>
  )
}

// ============================================================================
//  MODAL AVOIR (NOTE DE CRÉDIT)
// ============================================================================

function CreditNoteModal({
  invoice,
  onClose,
  onCreated,
}: {
  invoice: Invoice
  onClose: () => void
  onCreated: (creditNote: Invoice) => void
}) {
  const { addNotification } = useNotificationStore()
  const [reason, setReason] = useState('')
  const [lines, setLines] = useState<CreateInvoiceLineInput[]>(
    invoice.lines.map(l => ({
      description: l.description,
      quantity:    l.quantity,
      unitPrice:   l.unitPrice,
      discount:    l.discount,
      taxRuleCode: l.taxRule?.code,
      accountCode: l.accountCode,
    }))
  )

  const { mutate, isPending } = useMutation({
    mutationFn: () => invoicesApi.createCreditNote({
      originalInvoiceId: invoice.id,
      reason: reason.trim(),
      lines,
      notes: `Avoir sur ${invoice.number}`,
    }),
    onSuccess: onCreated,
    onError: (e: any) => addNotification({ id: Date.now().toString(), title: 'Erreur', message: e.response?.data?.message ?? 'Erreur de création', type: 'error' }),
  })

  const updateLine = (i: number, key: string, val: any) =>
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [key]: val } : l))

  const totalHt = lines.reduce((s, l) => s + (l.quantity * l.unitPrice * (1 - (l.discount ?? 0) / 100)), 0)

  const cls = 'w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]'

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.4)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)] shrink-0">
          <div>
            <h2 className="text-base font-bold text-[var(--brand-dark)]">Créer un avoir</h2>
            <p className="text-xs text-[var(--tx3)] mt-0.5">Sur la facture <span className="font-mono font-bold text-[var(--blue-d)]">{invoice.number}</span></p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bd)] rounded-lg">
            <X className="w-4 h-4 text-[var(--tx3)]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Raison */}
          <div>
            <label className="text-xs font-medium text-[var(--tx3)] block mb-1.5">Motif de l'avoir *</label>
            <input
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Ex: Retour marchandise, erreur de facturation…"
              className={cls}
              autoFocus
            />
          </div>

          {/* Lignes */}
          <div>
            <p className="text-xs font-medium text-[var(--tx3)] mb-2">Lignes de l'avoir</p>
            <div className="space-y-2">
              {lines.map((line, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center p-2 bg-[var(--surface-2)] rounded-lg border border-[var(--bd)]">
                  <input
                    value={line.description}
                    onChange={e => updateLine(i, 'description', e.target.value)}
                    placeholder="Description"
                    className="col-span-5 px-2 py-1.5 bg-white border border-[var(--bd)] rounded text-xs text-[var(--brand-dark)] focus:outline-none"
                  />
                  <input
                    type="number"
                    value={line.quantity}
                    onChange={e => updateLine(i, 'quantity', Number(e.target.value))}
                    className="col-span-2 px-2 py-1.5 bg-white border border-[var(--bd)] rounded text-xs text-center focus:outline-none"
                  />
                  <input
                    type="number"
                    value={line.unitPrice}
                    onChange={e => updateLine(i, 'unitPrice', Number(e.target.value))}
                    className="col-span-3 px-2 py-1.5 bg-white border border-[var(--bd)] rounded text-xs text-right focus:outline-none"
                  />
                  <span className="col-span-2 text-xs text-[#059669] font-medium text-right">
                    {(line.quantity * line.unitPrice * (1 - (line.discount ?? 0) / 100)).toLocaleString('fr-SN')}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-2 pt-2 border-t border-[var(--bd)]">
              <p className="text-sm font-bold text-[var(--brand-dark)]">
                Total HT : <span className="text-[#DC2626]">{totalHt.toLocaleString('fr-SN')} {invoice.currency}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t border-[var(--bd)] shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--brand-dark)]">
            Annuler
          </button>
          <button
            onClick={() => reason.trim() && mutate()}
            disabled={!reason.trim() || isPending}
            className="flex items-center gap-2 px-5 py-2 bg-[#DC2626] hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {isPending ? <Spinner /> : <Receipt className="w-4 h-4" />}
            Créer l'avoir
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
//  NOUVELLE FACTURE
// ============================================================================

function NewInvoicePage() {
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const { addNotification } = useNotificationStore()

  const [form, setForm] = useState<CreateInvoiceInput>({
    type:       'INVOICE',
    clientName: '',
    lines:      [{ description: '', quantity: 1, unitPrice: 0 }],
  })

  // Règles de TVA de vente (source unique des taux : TaxRule côté backend, via /invoices/tax-rules)
  const {
    data: taxRules = [],
    isLoading: taxRulesLoading,
    isError: taxRulesError,
  } = useQuery<SalesTaxRule[]>({
    queryKey:  ['invoice-tax-rules'],
    queryFn:   () => invoicesApi.getTaxRules(),
    staleTime: 30 * 60 * 1000,
  })

  // Taux (en %) applicable à un code de règle ; 0 si exonérée ou règle inconnue
  const ratePct = (code?: string) => {
    if (!code) return 0
    const rule = taxRules.find(r => r.code === code)
    if (!rule || rule.isExempt) return 0
    return toNum(rule.rate)
  }

  const createMutation = useMutation({
    mutationFn: () => invoicesApi.create(form),
    onSuccess: (invoice) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice-stats'] })
      addNotification({ id: Date.now().toString(), title: 'Facture créée', message: `Brouillon ${invoice.number}`, type: 'success' })
      navigate(`/invoices/${invoice.id}`)
    },
    onError: (e: any) => addNotification({ id: Date.now().toString(), title: 'Erreur', message: e.response?.data?.message ?? 'Erreur de création', type: 'error' }),
  })

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { description: '', quantity: 1, unitPrice: 0 }] }))
  const removeLine = (i: number) => setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }))
  const updateLine = (i: number, key: string, val: any) =>
    setForm(f => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [key]: val } : l) }))

  // Aperçu aligné sur l'arrondi backend (XOF, entiers) : brut, remise, HT puis TVA arrondis par ligne
  const previewLines = form.lines.map(l => {
    const gross = Math.round(l.quantity * l.unitPrice)
    const ht    = gross - Math.round(gross * (l.discount ?? 0) / 100)
    const tva   = Math.round(ht * ratePct(l.taxRuleCode) / 100)
    return { ht, tva }
  })
  const totalHt  = previewLines.reduce((s, l) => s + l.ht, 0)
  const totalTva = previewLines.reduce((s, l) => s + l.tva, 0)
  const totalTtc = totalHt + totalTva

  // TVA facturée mais NINEA absent : avertissement non bloquant (CGI SN art. 356)
  const hasTaxedLine  = form.lines.some(l => ratePct(l.taxRuleCode) > 0)
  const showNineaWarn = hasTaxedLine && !form.clientNinea?.trim()

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]"><ArrowLeft className="w-4 h-4" /></button>
        <h1 className="text-xl font-bold text-[var(--brand-dark)]">Nouvelle facture</h1>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-4">

          {/* Type + infos client */}
          <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[var(--tx3)] block mb-1">Type de document</label>
                <select
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value as any }))}
                  className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="INVOICE">Facture</option>
                  <option value="PROFORMA">Proforma</option>
                  <option value="ADVANCE">Acompte</option>
                  <option value="DEBIT_NOTE">Note de débit</option>
                </select>
              </div>
            </div>

            <div className="border-t border-[var(--bd)] pt-4">
              <h3 className="text-xs font-semibold text-[var(--tx3)] mb-3 flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5" /> Client
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs text-[var(--tx3)] block mb-1">Raison sociale *</label>
                  <input
                    value={form.clientName}
                    onChange={e => setForm(f => ({ ...f, clientName: e.target.value }))}
                    placeholder="Nom du client"
                    className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="new-invoice-ninea" className="text-xs text-[var(--tx3)] block mb-1">NINEA <span className="text-[var(--tx3)]">(recommandé si TVA)</span></label>
                  <input
                    id="new-invoice-ninea"
                    value={form.clientNinea || ''}
                    onChange={e => setForm(f => ({ ...f, clientNinea: e.target.value }))}
                    placeholder="1234567AB"
                    aria-describedby={showNineaWarn ? 'new-invoice-ninea-warn' : undefined}
                    className={`w-full px-3 py-2 bg-white border rounded-lg text-[var(--brand-dark)] font-mono text-sm focus:outline-none focus:border-blue-500 ${showNineaWarn ? 'border-[#F59E0B]' : 'border-[var(--bd)]'}`}
                  />
                  <div aria-live="polite">
                    {showNineaWarn && (
                      <p id="new-invoice-ninea-warn" className="mt-1.5 flex items-start gap-1.5 text-xs text-[#B45309]">
                        <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0 text-[#D97706]" aria-hidden="true" />
                        <span>NINEA manquant : la facture portera la TVA, mais le client ne pourra pas la déduire (CGI SN art. 356).</span>
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[var(--tx3)] block mb-1">RCCM</label>
                  <input
                    value={form.clientRccm || ''}
                    onChange={e => setForm(f => ({ ...f, clientRccm: e.target.value }))}
                    placeholder="SN DKR XXXX"
                    className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] font-mono text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-[var(--tx3)] block mb-1">Email <span className="text-[var(--blue)]">(requis pour envoi email)</span></label>
                  <input
                    type="email"
                    value={form.clientEmail || ''}
                    onChange={e => setForm(f => ({ ...f, clientEmail: e.target.value }))}
                    placeholder="email@client.com"
                    className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--tx)] text-sm focus:outline-none focus:border-[var(--blue)]"
                  />
                </div>
                <div>
                  <label className="text-xs text-[var(--tx3)] block mb-1">Adresse client</label>
                  <input
                    value={(form as any).clientAddress || ''}
                    onChange={e => setForm(f => ({ ...f, clientAddress: e.target.value } as any))}
                    placeholder="Rue, Quartier, Ville"
                    className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--tx)] text-sm focus:outline-none focus:border-[var(--blue)]"
                  />
                </div>
                <div>
                  <label className="text-xs text-[var(--tx3)] block mb-1">Échéance</label>
                  <input
                    type="date"
                    value={form.dueDate || ''}
                    onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
                    className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Lignes */}
          <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-[var(--bd)] flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[var(--tx2)]">Lignes</h3>
              {taxRulesError && (
                <p id="new-invoice-tax-error" role="status" className="flex items-center gap-1 text-xs text-[var(--tx3)]">
                  <AlertCircle className="w-3.5 h-3.5 text-[#D97706]" aria-hidden="true" />
                  Taux de TVA indisponibles : seules les lignes « Sans TVA » sont possibles.
                </p>
              )}
              <button onClick={addLine} className="flex items-center gap-1 text-xs text-[var(--blue-d)] hover:text-blue-300">
                <Plus className="w-3.5 h-3.5" /> Ajouter
              </button>
            </div>
            <div className="divide-y divide-[var(--bd)]">
              {form.lines.map((line, i) => (
                <div key={i} className="px-5 py-4 grid grid-cols-12 gap-3 items-end">
                  <div className="col-span-5">
                    {i === 0 && <label className="text-xs text-[var(--tx3)] block mb-1">Description</label>}
                    <input
                      value={line.description}
                      onChange={e => updateLine(i, 'description', e.target.value)}
                      placeholder="Désignation du produit/service"
                      className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <label className="text-xs text-[var(--tx3)] block mb-1">Quantité</label>}
                    <input
                      type="number" min="0"
                      value={toNum(line.quantity)}
                      onChange={e => updateLine(i, 'quantity', Number(e.target.value))}
                      className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <label className="text-xs text-[var(--tx3)] block mb-1">P.U. HT</label>}
                    <input
                      type="number" min="0"
                      value={toNum(line.unitPrice)}
                      onChange={e => updateLine(i, 'unitPrice', Number(e.target.value))}
                      className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <label htmlFor={`new-invoice-tax-${i}`} className="text-xs text-[var(--tx3)] block mb-1">TVA</label>}
                    <select
                      id={`new-invoice-tax-${i}`}
                      aria-label={i === 0 ? undefined : `TVA de la ligne ${i + 1}`}
                      value={line.taxRuleCode || ''}
                      onChange={e => updateLine(i, 'taxRuleCode', e.target.value || undefined)}
                      disabled={taxRulesLoading}
                      aria-busy={taxRulesLoading || undefined}
                      aria-describedby={taxRulesError ? 'new-invoice-tax-error' : undefined}
                      className="w-full px-2 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] text-sm focus:outline-none focus:border-blue-500 disabled:opacity-60 disabled:cursor-wait"
                    >
                      {taxRulesLoading ? (
                        <option value={line.taxRuleCode || ''}>Chargement…</option>
                      ) : (
                        <>
                          <option value="">Sans TVA</option>
                          {taxRules.map(rule => (
                            <option key={rule.code} value={rule.code}>{rule.name}</option>
                          ))}
                        </>
                      )}
                    </select>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {form.lines.length > 1 && (
                      <button onClick={() => removeLine(i)} className="text-[var(--tx3)] hover:text-[#DC2626] mt-5">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Récapitulatif */}
        <div className="space-y-4">
          <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-4 space-y-3 sticky top-6">
            <h3 className="text-sm font-semibold text-[var(--tx3)]">Récapitulatif</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[var(--tx3)]">Total HT</span><span className="text-[var(--brand-dark)]">{fmtMoney(totalHt)}</span></div>
              <div className="flex justify-between"><span className="text-[var(--tx3)]">TVA</span><span className="text-blue-300">{fmtMoney(totalTva)}</span></div>
              <div className="flex justify-between border-t border-[var(--bd)] pt-2">
                <span className="font-semibold text-[var(--tx2)]">Total TTC</span>
                <span className="font-black text-[var(--brand-dark)] text-lg">{fmtMoney(totalTtc)}</span>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs text-[var(--tx3)] block mb-1">Notes internes</label>
              <textarea
                value={form.notes || ''}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] text-sm resize-none focus:outline-none focus:border-blue-500"
              />
            </div>

            <button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending || !form.clientName || !form.lines.every(l => l.description)}
              className="w-full py-2.5 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {createMutation.isPending ? <Spinner /> : <FileText className="w-4 h-4" />}
              Créer le brouillon
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
//  EXPORT FEC
// ============================================================================

function FecExportPage() {
  const navigate  = useNavigate()
  const [fiscalYearId, setFiscalYearId] = useState('')
  const [result, setResult] = useState<any>(null)

  const { data: fiscalYears } = useQuery({
    queryKey: ['fiscal-years-for-fec'],
    queryFn: () => import('@/lib/axios').then(m => m.default.get('/accounting/fiscal-years').then(r => r.data.data ?? r.data)),
  })

  const exportMutation = useMutation({
    mutationFn: () => invoicesApi.exportFec(fiscalYearId),
    onSuccess: (data) => setResult(data),
  })

  const downloadCsv = () => {
    if (!result) return
    const headers = ['JournalCode', 'JournalLabel', 'EntryNumber', 'EntryDate', 'AccountCode', 'AccountLabel', 'Debit', 'Credit', 'Description', 'Reference']
    const rows = result.lines.map((l: any) =>
      [l.journalCode, l.journalLabel, l.entryNumber, l.entryDate, l.accountCode, l.accountLabel, l.debit, l.credit, `"${l.description}"`, l.reference || ''].join(';')
    )
    const csv  = [headers.join(';'), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url; a.download = `FEC-${result.fiscalYear.year}-${result.generatedAt.split('T')[0]}.csv`
    a.click(); URL.revokeObjectURL(url)
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-[var(--tx3)] hover:text-[var(--brand-dark)]"><ArrowLeft className="w-4 h-4" /></button>
        <div>
          <h1 className="text-xl font-bold text-[var(--brand-dark)]">Export FEC</h1>
          <p className="text-sm text-[var(--tx3)]">Fichier des Écritures Comptables — Pack Contrôleur DGI</p>
        </div>
      </div>

      <div className="bg-[#FEF3C7] border border-[rgba(245,158,11,0.25)] rounded-xl p-4 text-sm text-amber-300">
        <p className="font-semibold mb-1">⚖️ Obligation légale (CGI SN art. 931)</p>
        <p className="text-[#D97706]/80">Ce fichier doit pouvoir être généré en moins de 4h sur demande de la DGI. Il contient toutes les écritures validées de l'exercice.</p>
      </div>

      <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-5 space-y-4">
        <div>
          <label className="text-xs text-[var(--tx3)] block mb-2">Exercice fiscal</label>
          <select
            value={fiscalYearId}
            onChange={e => setFiscalYearId(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-[var(--brand-dark)] text-sm focus:outline-none focus:border-blue-500"
          >
            <option value="">Sélectionner un exercice…</option>
            {(fiscalYears as any[])?.map((fy: any) => (
              <option key={fy.id} value={fy.id}>{fy.label} ({fy.year})</option>
            ))}
          </select>
        </div>

        <button
          onClick={() => exportMutation.mutate()}
          disabled={!fiscalYearId || exportMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {exportMutation.isPending ? <Spinner /> : <Download className="w-4 h-4" />}
          Générer le FEC
        </button>
      </div>

      {result && (
        <div className="bg-[var(--surface-2)] border border-[rgba(16,185,129,0.25)] rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[var(--brand-dark)] font-semibold">FEC généré — {result.fiscalYear.label}</p>
              <p className="text-sm text-[var(--tx3)]">{result.totalLines} lignes · {new Date(result.generatedAt).toLocaleString('fr-FR')}</p>
            </div>
            <button onClick={downloadCsv} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-[#10B981] text-[var(--brand-dark)] rounded-lg text-sm font-medium transition-colors">
              <Download className="w-4 h-4" /> Télécharger CSV
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
//  ROUTER MODULE
// ============================================================================

export default function InvoicesPages() {
  return (
    <Routes>
      <Route index           element={<InvoiceDashboard />} />
      <Route path="list"     element={<InvoiceListPage />} />
      <Route path="new"      element={<NewInvoicePage />} />
      <Route path="export-fec" element={<FecExportPage />} />
      <Route path=":id"      element={<InvoiceDetailPage />} />
    </Routes>
  )
}
